import { useMemo, useState, type ReactElement } from "react";
import { useForm } from "@tanstack/react-form";
import { useSearch } from "@tanstack/react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { MarketingLayout } from "./sections/Layout";

function fallbackTenantUrl(site: string): string {
  const host = window.location.hostname.replace(/^www\./, "");
  return `https://${site}.${host}/`;
}

function hashReturnUrl(): string | null {
  const raw = window.location.hash.slice(1);
  if (!raw) return null;
  return `https://${raw}`;
}

function siteFromHash(): string {
  try {
    const url = new URL(hashReturnUrl() ?? "");
    return url.hostname.split(".")[0] ?? "";
  } catch {
    return "";
  }
}

function safeReturnUrl(rawNext: string | null, site: string): string {
  if (!site || !/^[a-z0-9-]+$/.test(site)) return "/";
  const fallback = fallbackTenantUrl(site);
  const candidate = rawNext ?? hashReturnUrl();
  if (!candidate) return fallback;

  try {
    const url = new URL(candidate);
    const host = window.location.hostname.replace(/^www\./, "");
    const expectedTenant = `${site}.${host}`;
    if (url.protocol === "https:" && url.hostname === expectedTenant) {
      return url.toString();
    }
  } catch {
    return fallback;
  }

  return fallback;
}

function displaySiteName(site: string): string {
  if (!site) return "This site";
  return site
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function attemptKey(returnUrl: string): string {
  return `chill-preview-unlock:${returnUrl}`;
}

export function UnlockPreview(): ReactElement {
  const search = useSearch({ from: "/unlock" });

  const site = search.site ?? siteFromHash();
  const returnUrl = useMemo(
    () => safeReturnUrl(search.next ?? null, site),
    [search.next, site],
  );
  const [badPassword] = useState(
    () =>
      Number(search.bad) === 1 ||
      window.sessionStorage.getItem(attemptKey(returnUrl)) === "1",
  );
  const form = useForm({
    defaultValues: {
      password: "",
    },
    onSubmit: ({ value }) => {
      const next = new URL(returnUrl);
      next.searchParams.set("pw", value.password);
      window.sessionStorage.setItem(attemptKey(returnUrl), "1");
      window.location.assign(next.toString());
    },
  });

  return (
    <MarketingLayout>
      <section className="mx-auto flex min-h-[70vh] w-full max-w-md items-center px-6 py-16">
        <Card className="w-full">
          <CardContent className="p-8">
            <div className="mb-3 text-xs font-semibold uppercase tracking-widest text-primary">
              Private preview
            </div>
            <h1 className="text-3xl font-semibold tracking-tight">
              Site preview locked
            </h1>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">
              {displaySiteName(site)} is password protected until the owner
              publishes it.
            </p>

            {badPassword ? (
              <div className="mt-5 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                That password did not work. Try again.
              </div>
            ) : null}

            <form
              onSubmit={(event) => {
                event.preventDefault();
                event.stopPropagation();
                void form.handleSubmit();
              }}
              className="mt-6 space-y-4"
            >
              <form.Field name="password">
                {(field) => (
                  <div className="space-y-2">
                    <Label htmlFor="preview-password">Password</Label>
                    <Input
                      id="preview-password"
                      name={field.name}
                      type="password"
                      autoComplete="current-password"
                      autoFocus
                      required
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.currentTarget.value)
                      }
                    />
                  </div>
                )}
              </form.Field>
              <Button type="submit" className="w-full">
                Unlock preview
              </Button>
            </form>
          </CardContent>
        </Card>
      </section>
    </MarketingLayout>
  );
}
